import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  ModalSubtitle,
  CloseButton,
  ModalBody,
  ModalFooter,
} from './StyledComponents';

// Close Icon Component
const CloseIcon = ({ size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill={color}>
    <path
      fillRule="evenodd"
      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
      clipRule="evenodd"
    />
  </svg>
);

CloseIcon.propTypes = {
  size: PropTypes.number,
  color: PropTypes.string,
};

/**
 * ModalV2 - A reusable, theme-aware modal component
 *
 * @example
 * <ModalV2
 *   isOpen={isOpen}
 *   onClose={handleClose}
 *   title="Modal Title"
 *   subtitle="Optional subtitle"
 *   footer={<Button onClick={handleSave}>Save</Button>}
 * >
 *   <p>Modal content goes here</p>
 * </ModalV2>
 */
const ModalV2 = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
  maxWidth,
  closable = true,
  closeOnOverlayClick = true,
}) => {
  // Handle overlay click
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && closeOnOverlayClick) {
      onClose();
    }
  };

  // Handle ESC key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && closable && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, closable]);

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer maxWidth={maxWidth}>
        {closable && (
          <CloseButton onClick={onClose} aria-label="Close modal">
            <CloseIcon />
          </CloseButton>
        )}

        {(title || subtitle) && (
          <ModalHeader>
            {title && <ModalTitle hasSubtitle={!!subtitle}>{title}</ModalTitle>}
            {subtitle && <ModalSubtitle>{subtitle}</ModalSubtitle>}
          </ModalHeader>
        )}

        <ModalBody>{children}</ModalBody>

        {footer && <ModalFooter>{footer}</ModalFooter>}
      </ModalContainer>
    </ModalOverlay>
  );
};

ModalV2.propTypes = {
  /** Controls modal visibility */
  isOpen: PropTypes.bool.isRequired,
  /** Callback when modal should close */
  onClose: PropTypes.func.isRequired,
  /** Modal title (optional) */
  title: PropTypes.string,
  /** Modal subtitle/description (optional) */
  subtitle: PropTypes.string,
  /** Modal content */
  children: PropTypes.node.isRequired,
  /** Custom footer content (buttons, actions, etc.) */
  footer: PropTypes.node,
  /** Maximum width of modal (default: 600px) */
  maxWidth: PropTypes.string,
  /** Show close button (default: true) */
  closable: PropTypes.bool,
  /** Close modal when clicking overlay (default: true) */
  closeOnOverlayClick: PropTypes.bool,
};

export default ModalV2;
